// ── Widget slot descriptors ──────────────────────────────────────────────────
// For every widget type, enumerate the ID-BEARING locations in its spec that hold
// a metric id, a subject id (player/position/group/full-team), or a drill id, plus
// how to strip drills and other team-private data. This is the single source of
// truth the template tokenizer (export) and resolver (import) walk. See
// Docs/TEMPLATE_SYSTEM.md §8.
//
// Each descriptor is exported and referenced by its widget's definition via
// `WidgetDefinition.slots`, so the full per-type contract lives on the definition.
// The implementations stay in THIS component-free module (not the definition files)
// because the template (de)serializer walks them via `getWidgetSlots` and must not
// pull in the widget component / PDF graph that the widget registry imports.
//
// Names/titles are NOT enumerated here — they are handled by a global token regex
// pass over the serialized spec (metric/subject display names round-trip as tokens;
// literal "ACWR" suffixes survive as text around the metric token).
//
// Metric/subject ids live in irreducibly heterogeneous shapes (per-series builder
// misc, widget-level arrays, a queryId-keyed Record, top-level scalars, per-series
// scalars, a bare integer), so each ref exposes get/set closures rather than a path.

import type {
  ComponentSpec,
  ChartComponentSpec,
  PieChartComponentSpec,
  TableComponentSpec,
  Table2DComponentSpec,
  StatComponentSpec,
  RadarComponentSpec,
  GaugeComponentSpec,
  BodyMapComponentSpec,
  PlayerCardComponentSpec,
  PlayerNotesComponentSpec,
  DynamicControllerComponentSpec,
  ScatterplotComponentSpec,
} from "../Processing/types";
import type { GridWidgetType } from "../types";

export type SlotValue = string | number;

/** A single ID-bearing location in a spec. `get` reads the current value (a real id
 *  during export, a token during import); `set` writes the replacement. */
export interface SlotRef {
  get(): SlotValue | null | undefined;
  set(v: SlotValue): void;
  /** The field holds a bare player id (player_card.player_id) — "full-team" is not a
   *  legal value here, so the resolver drops the widget instead of assigning it. */
  requiresPlayer?: boolean;
}

export interface WidgetSlots<TSpec extends ComponentSpec = ComponentSpec> {
  /** Metric-id-bearing fields (bare id component only — the agg stays put). */
  metricRefs(spec: TSpec): SlotRef[];
  /** Subject-id-bearing fields: full-team / player- / pos- / group- (drill- is stripped, not tokenized). */
  subjectRefs(spec: TSpec): SlotRef[];
  /** Remove drill filters (team-scoped, non-portable): clear selectedDrills + reset the tab;
   *  for radar, drill-scoped per-series subjectFilters degrade to full-team. */
  stripDrills(spec: TSpec): void;
  /** Remove any other team-private ids that are not tokenizable (e.g. note ids). */
  stripPrivate?(spec: TSpec): void;
}

/** Explicit opt-out for widgets that carry no metric/subject/drill ids (title/text/logo/spacer). */
export const NoSlots = { __contract: "no-slots" } as const;
export type NoSlots = typeof NoSlots;

export function isNoSlots(s: WidgetSlots | NoSlots | undefined): s is NoSlots {
  return !!s && "__contract" in s;
}

// ── ref builders ─────────────────────────────────────────────────────────────

/** Ref to a scalar property on an object (skips silently if the container is missing).
 *  Typed loosely: the builder-misc interfaces have no index signature, and the id
 *  location varies per widget — the descriptors below supply the correct key. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function propRef(obj: any, key: string): SlotRef[] {
  if (!obj) return [];
  return [{ get: () => obj[key] as SlotValue | null | undefined, set: (v) => { obj[key] = v; } }];
}

/** Refs to every element of a string array (each element is one id). */
function arrayRefs(arr: unknown): SlotRef[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((_, i) => ({
    get: () => arr[i] as SlotValue,
    set: (v: SlotValue) => { arr[i] = v; },
  }));
}

/** Refs to every VALUE of a Record<string, id> (e.g. table columnMetrics keyed by queryId). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function objValueRefs(obj: any): SlotRef[] {
  if (!obj) return [];
  return Object.keys(obj).map((k) => ({
    get: () => obj[k] as SlotValue,
    set: (v: SlotValue) => { obj[k] = v; },
  }));
}

const SUBJECT_PREFIXES = /^(full-team|player-|pos-|group-)/;

/** Clear the standard drill fields on a builder-misc object. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function clearDrillFields(misc: any): void {
  if (!misc) return;
  if (Array.isArray(misc.selectedDrills)) misc.selectedDrills = [];
  if (misc.subjectActiveTab === "drills") misc.subjectActiveTab = "athletes";
}

// ── per-widget descriptors ───────────────────────────────────────────────────

export const chartSlots: WidgetSlots<ChartComponentSpec> = {
  metricRefs: (s) => s.series.flatMap((ser) => propRef(ser.series_builder_misc, "selectedMetricId")),
  subjectRefs: (s) => s.series.flatMap((ser) => arrayRefs(ser.series_builder_misc?.selectedSubjects)),
  stripDrills: (s) => s.series.forEach((ser) => clearDrillFields(ser.series_builder_misc)),
};

export const pieSlots: WidgetSlots<PieChartComponentSpec> = {
  metricRefs: (s) => s.series.flatMap((ser) => propRef(ser.series_builder_misc, "selectedMetricId")),
  subjectRefs: (s) => s.series.flatMap((ser) => arrayRefs(ser.series_builder_misc?.selectedSubjects)),
  stripDrills: (s) => s.series.forEach((ser) => clearDrillFields(ser.series_builder_misc)),
};

export const tableSlots: WidgetSlots<TableComponentSpec> = {
  metricRefs: (s) => objValueRefs(s.table_builder_misc?.columnMetrics),
  subjectRefs: (s) => arrayRefs(s.table_builder_misc?.selectedSubjects),
  stripDrills: (s) => clearDrillFields(s.table_builder_misc),
};

export const table2dSlots: WidgetSlots<Table2DComponentSpec> = {
  metricRefs: (s) => (s.table_2d_builder_misc?.metrics ?? []).flatMap((m) => propRef(m as any, "metricId")),
  subjectRefs: (s) => arrayRefs(s.table_2d_builder_misc?.selectedSubjects),
  stripDrills: (s) => clearDrillFields(s.table_2d_builder_misc),
};

export const statSlots: WidgetSlots<StatComponentSpec> = {
  metricRefs: (s) => propRef(s.stat_tile_builder_misc, "selectedMetricId"),
  subjectRefs: (s) => arrayRefs(s.stat_tile_builder_misc?.selectedSubjects),
  stripDrills: (s) => clearDrillFields(s.stat_tile_builder_misc),
};

export const radarSlots: WidgetSlots<RadarComponentSpec> = {
  metricRefs: (s) => (s.radar_builder_misc?.metrics ?? []).flatMap((m) => propRef(m as any, "metricId")),
  // Radar keeps ONE subjectFilter string per series (not an array).
  subjectRefs: (s) => s.series.flatMap((ser) => propRef(ser as any, "subjectFilter")),
  // Radar has no selectedDrills; a drill-scoped series subjectFilter degrades to full-team.
  stripDrills: (s) => s.series.forEach((ser) => {
    if (typeof ser.subjectFilter === "string" && ser.subjectFilter.startsWith("drill-")) {
      ser.subjectFilter = "full-team";
    }
  }),
};

/** Gauge's top-level `metric` is a fused `"<id>-<agg>"` string — the ref exposes/rewrites
 *  only the id component, preserving the agg suffix. */
function gaugeFusedMetricRef(spec: GaugeComponentSpec): SlotRef[] {
  const splitId = (fused: string): string => {
    const i = fused.lastIndexOf("-");
    return i > 0 ? fused.slice(0, i) : fused;
  };
  const aggOf = (fused: string): string => {
    const i = fused.lastIndexOf("-");
    return i > 0 ? fused.slice(i + 1) : "avg";
  };
  return [{
    get: () => (spec.metric ? splitId(spec.metric) : spec.metric),
    set: (v) => { spec.metric = `${v}-${spec.metric ? aggOf(spec.metric) : "avg"}`; },
  }];
}

export const gaugeSlots: WidgetSlots<GaugeComponentSpec> = {
  metricRefs: (s) => [
    ...propRef(s.gauge_builder_misc, "selectedMetricId"),
    ...gaugeFusedMetricRef(s),
  ],
  subjectRefs: (s) => [
    ...arrayRefs(s.gauge_builder_misc?.selectedSubjects),
    ...arrayRefs(s.gauge_builder_misc?.scaleSubjectFilter),
  ],
  stripDrills: (s) => clearDrillFields(s.gauge_builder_misc),
};

export const bodyMapSlots: WidgetSlots<BodyMapComponentSpec> = {
  // question_key is a fixed wellness enum (portable across teams) — not tokenized.
  metricRefs: () => [],
  subjectRefs: (s) => propRef(s.body_map_builder_misc, "playerSubject"),
  stripDrills: () => {},
};

/** player_card stores a BARE integer player_id — normalize to/from the `player-<id>` form. */
export const playerCardSlots: WidgetSlots<PlayerCardComponentSpec> = {
  metricRefs: () => [],
  subjectRefs: (s) => [{
    get: () => {
      const v = s.player_id as number | string | null | undefined;
      if (v == null) return undefined;
      // Bare integer id → canonical `player-<id>`; a template token ("{PL_1}") must pass
      // through untouched or the resolver's anchored token match can never bind it.
      return /^\d+$/.test(String(v)) ? `player-${v}` : String(v);
    },
    set: (v) => {
      const m = /^player-(\d+)$/.exec(String(v));
      // Real id on import → number; token on export → keep the string placeholder.
      (s as { player_id: number }).player_id = (m ? Number(m[1]) : v) as number;
    },
    requiresPlayer: true,
  }],
  stripDrills: () => {},
};

export const scatterplotSlots: WidgetSlots<ScatterplotComponentSpec> = {
  // Each axis keeps its metric id in axis_builder_misc.selectedMetricId (the field
  // name the v1 template processor also string-replaces on).
  metricRefs: (s) => [
    ...propRef(s.xAxis?.axis_builder_misc, "selectedMetricId"),
    ...propRef(s.yAxis.axis_builder_misc, "selectedMetricId"),
  ],
  subjectRefs: (s) => arrayRefs(s.scatterplot_builder_misc?.selectedSubjects),
  stripDrills: (s) => {
    clearDrillFields(s.scatterplot_builder_misc);
    // A drill entity groupby is team-private the same way a drill filter is.
    if (s.scatterplot_builder_misc?.groupBy === "drill") {
      s.scatterplot_builder_misc.groupBy = "player";
    }
  },
};

export const playerNotesSlots: WidgetSlots<PlayerNotesComponentSpec> = {
  metricRefs: () => [],
  subjectRefs: (s) => arrayRefs(s.player_notes_builder_misc?.selectedSubjects),
  stripDrills: () => {},
  stripPrivate: (s) => {
    if (s.player_notes_builder_misc && Array.isArray(s.player_notes_builder_misc.noteIds)) {
      s.player_notes_builder_misc.noteIds = [];
    }
    if (s.player_notes_builder_misc?.searchPattern) s.player_notes_builder_misc.searchPattern = "";
  },
};

/** The controller's picker state holds the same subject ids a widget filter does. */
export const dynamicControllerSlots: WidgetSlots<DynamicControllerComponentSpec> = {
  metricRefs: () => [],
  subjectRefs: (s) => arrayRefs(s.slicer_builder_misc?.selectedSubjects),
  stripDrills: (s) => clearDrillFields(s.slicer_builder_misc),
};

// ── registry ─────────────────────────────────────────────────────────────────
// This lean, component-free map is the (de)serializer's lookup: `Templates/` walks it
// via `getWidgetSlots` without importing the widget registry (which would pull in every
// widget's React component + PDF renderer). Each widget definition ALSO references its
// descriptor through `WidgetDefinition.slots`; the precise per-key typing below means
// neither this map nor a definition can hold the wrong descriptor for a type without a
// compile error, so the two views can't silently drift apart.

/** Widget type discriminant → its component spec, e.g. `"chart"` → `ChartComponentSpec`. */
type SpecForType = { [S in ComponentSpec as S["type"]]: S };

/** Every widget type mapped to its OWN slot descriptor, or the `NoSlots` opt-out. */
type SlotsRegistry = { [T in GridWidgetType]: WidgetSlots<SpecForType[T]> | NoSlots };

const SLOTS_BY_TYPE: SlotsRegistry = {
  chart: chartSlots,
  pie_chart: pieSlots,
  table: tableSlots,
  table_2d: table2dSlots,
  stat_tile: statSlots,
  radar: radarSlots,
  gauge: gaugeSlots,
  body_map: bodyMapSlots,
  player_card: playerCardSlots,
  player_notes: playerNotesSlots,
  slicer: dynamicControllerSlots,
  scatterplot: scatterplotSlots,
  title: NoSlots,
  text: NoSlots,
  logo: NoSlots,
  spacer: NoSlots,
  ai_insights: NoSlots,
};

export function getWidgetSlots(type: GridWidgetType): WidgetSlots | NoSlots | undefined {
  return SLOTS_BY_TYPE[type];
}

/** True when this subject value must be stripped (drill) rather than tokenized. */
export function isDrillSubject(v: SlotValue | null | undefined): boolean {
  return typeof v === "string" && v.startsWith("drill-");
}

/** Classify a subject id string into its token kind (or null for full-team / unknown). */
export function subjectKind(v: SlotValue | null | undefined): "player" | "position" | "group" | null {
  if (typeof v !== "string") return null;
  if (v.startsWith("player-")) return "player";
  if (v.startsWith("pos-")) return "position";
  if (v.startsWith("group-")) return "group";
  return null;
}

export { SUBJECT_PREFIXES };
